import { MapPin } from 'lucide-react'
import type { TracksIndexProps, TracksIndexTrack } from '@/types/tracks_index'
import { useLikes } from '@/shared/likes/useLikes'
import MapListIndex from '@/shared/map_index/MapListIndex'
import type { MapMarker } from '@/shared/map_index/MapListIndex'
import ResultCard from '@/shared/map_index/ResultCard'

export default function TracksIndex({ favorites, visited, nearby, map_center, zoom, search, distance }: TracksIndexProps) {
  const { likeIdFor, toggleLike } = useLikes('Track')

  // A track is "mine" if it's a favorite or somewhere I've been.
  const mineIds = new Set([...favorites, ...visited].map(track => track.id))

  const markers: MapMarker[] = [...favorites, ...visited, ...nearby].map(track => ({
    mine: mineIds.has(track.id),
    longitude: track.longitude,
    latitude: track.latitude,
    popupHtml: `<a href="/tracks/${track.slug}"><strong>${track.name}</strong><p>${track.city}, ${track.state}${track.distance != null ? ` (${track.distance.toPrecision(4)} mi)` : ''}</p><p>Click for more info</p></a>`,
  }))

  return (
    <MapListIndex<TracksIndexTrack>
      title="Tracks"
      path="/tracks"
      markers={markers}
      sections={[
        { label: 'Your tracks', items: favorites },
        { label: 'Visited', items: visited },
        { label: 'Near you', items: nearby },
      ]}
      emptyMessage="No tracks found. Try widening your search."
      mapCenter={map_center}
      zoom={zoom}
      initialSearch={search}
      initialDistance={distance}
      coordsFor={track => [track.longitude, track.latitude]}
      renderItem={(track, { selected, onSelect }) => (
        <ResultCard
          name={track.name}
          selected={selected}
          onSelect={onSelect}
          liked={likeIdFor(track) != null}
          onToggleLike={() => toggleLike(track)}
          href={`/tracks/${track.slug}`}
          ctaLabel="Visit"
        >
          <p className="flex items-center gap-1.5 text-sm text-gray11">
            <MapPin className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">
              {track.city}, {track.state}
              {track.distance != null && ` · ${track.distance.toFixed(1)} mi`}
            </span>
          </p>
        </ResultCard>
      )}
    />
  )
}
